import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { type CSSProperties, useState } from "react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import type { DashboardDay } from "../../model/TeamDashboard";
import { DashboardCard } from "./DashboardCard";
import { dayDate } from "./dayDate";

// Поле графика в единицах viewBox: линии тянутся по ширине, точки и подписи — в HTML
// процентами, поэтому не искажаются.
const WIDTH = 1000;
const HEIGHT = 220;
// Сколько подписей дней на оси X, не больше.
const X_TICKS = 6;

type Series = "active" | "new_players";

const ACTIVE_COUNT = msg({ message: "{count, plural, one {# active} other {# active}}" });
const NEW_COUNT = msg({ message: "{count, plural, one {# new} other {# new}}" });
const ACTIVE_WORD = msg({ message: "{count, plural, one {active} other {active}}" });
const NEW_WORD = msg({ message: "{count, plural, one {new} other {new}}" });

// eslint-disable-next-line lingui/no-unlocalized-strings -- классы, а не текст
const DOT = "pointer-events-none absolute size-2.5 -translate-1/2 rounded-full ring-2 ring-surface";
// eslint-disable-next-line lingui/no-unlocalized-strings -- классы, а не текст
const END_LABEL = "absolute -translate-y-1/2 pl-3 text-xs whitespace-nowrap text-foreground-soft";

// Шаг сетки: 5, 10, 20, 50… — чтобы делений было не больше пяти.
const gridStep = (max: number): number => {
  for (const step of [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000]) {
    if (max / step <= 5) {
      return step;
    }
  }
  return Math.ceil(max / 5);
};

type PlayersChartProps = {
  days: DashboardDay[];
};

// «Игроки по дням»: активные (синяя линия) и новые (оранжевая) — один график, одна ось:
// единица у обоих — игроки. Наведение показывает день целиком; рядом — таблица тех же чисел.
export const PlayersChart = ({ days }: PlayersChartProps) => {
  const { i18n } = useLingui();
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const short = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "short",
    // eslint-disable-next-line lingui/no-unlocalized-strings -- часовой пояс, а не текст
    timeZone: "UTC",
  });
  const long = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "long",
    weekday: "short",
    // eslint-disable-next-line lingui/no-unlocalized-strings -- часовой пояс, а не текст
    timeZone: "UTC",
  });

  const last = days.at(-1);
  const lastActive = last?.active ?? null;
  const lastNew = last?.new_players ?? null;
  const peak = Math.max(1, ...days.map((day) => Math.max(day.active, day.new_players)));
  const step = gridStep(peak);
  const top = Math.ceil(peak / step) * step;
  const x = (index: number) => (days.length <= 1 ? WIDTH / 2 : (index / (days.length - 1)) * WIDTH);
  const y = (value: number) => HEIGHT - (value / top) * HEIGHT;
  const path = (series: Series) =>
    days
      .map(
        (day, index) =>
          // eslint-disable-next-line lingui/no-unlocalized-strings -- команды пути SVG, а не текст
          `${index === 0 ? "M" : "L"}${x(index).toFixed(1)} ${y(day[series]).toFixed(1)}`,
      )
      .join(" ");
  const ticks = Array.from({ length: top / step + 1 }, (_, index) => index * step);
  const xEvery = Math.max(1, Math.ceil(days.length / X_TICKS));
  const at = (index: number, value: number): CSSProperties => ({
    left: `${String((x(index) / WIDTH) * 100)}%`,
    top: `${String((1 - value / top) * 100)}%`,
  });
  const hovered = hover === null ? undefined : days[hover];

  return (
    <DashboardCard
      title={<Trans>Players by day</Trans>}
      aside={
        <div className="flex flex-wrap items-center gap-x-4.5 gap-y-2 text-[13px] text-muted">
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-0.5 w-4 rounded-full bg-chart-blue" />
            <Trans>Active — used Relock that day</Trans>
          </span>
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-0.5 w-4 rounded-full bg-chart-amber" />
            <Trans>New — first sign-in with Steam</Trans>
          </span>
          <Button
            variant="outline"
            size="sm"
            aria-pressed={asTable}
            onClick={() => {
              setAsTable(!asTable);
            }}
          >
            {asTable ? <Trans>As chart</Trans> : <Trans>As table</Trans>}
          </Button>
        </div>
      }
    >
      {asTable ? (
        <div className="max-h-64 overflow-auto rounded-[10px] border border-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-surface-raised text-left text-muted">
                <th scope="col" className="px-3 py-2 font-semibold">
                  <Trans>Day</Trans>
                </th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  <Trans>Active</Trans>
                </th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  <Trans>New</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {[...days].reverse().map((day) => (
                <tr key={day.day} className="border-t border-border/60">
                  <td className="px-3 py-1.5">{long.format(dayDate(day.day))}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{day.active}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {day.new_players}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          className="relative h-64"
          onMouseLeave={() => {
            setHover(null);
          }}
        >
          <div aria-hidden="true" className="absolute top-0 bottom-[30px] left-0 w-[34px]">
            {ticks.map((tick) => (
              <span
                key={tick}
                className="absolute right-0 -translate-y-1/2 font-mono text-[11px] text-subtle tabular-nums"
                style={{ top: `${String((1 - tick / top) * 100)}%` }}
              >
                {tick}
              </span>
            ))}
          </div>
          <div className="absolute top-0 right-[104px] bottom-[30px] left-[44px]">
            <svg
              aria-hidden="true"
              viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
              preserveAspectRatio="none"
              className="absolute inset-0 size-full overflow-visible"
            >
              {ticks.slice(1).map((tick) => (
                <path
                  key={tick}
                  d={`M0 ${y(tick).toFixed(1)}H${String(WIDTH)}`}
                  className="stroke-border/50"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              <path
                d={`M0 ${String(HEIGHT)}H${String(WIDTH)}`}
                className="stroke-border"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={path("active")}
                className="fill-none stroke-chart-blue"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={path("new_players")}
                className="fill-none stroke-chart-amber"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            {last !== undefined && lastActive !== null && lastNew !== null && (
              <>
                <span
                  aria-hidden="true"
                  className={cn(DOT, "bg-chart-blue")}
                  style={at(days.length - 1, last.active)}
                />
                <span
                  aria-hidden="true"
                  className={cn(DOT, "bg-chart-amber")}
                  style={at(days.length - 1, last.new_players)}
                />
                <span
                  className={END_LABEL}
                  style={{ ...at(days.length - 1, last.active), left: "100%" }}
                >
                  {i18n._({ ...ACTIVE_COUNT, values: { count: lastActive } })}
                </span>
                <span
                  className={END_LABEL}
                  style={{ ...at(days.length - 1, last.new_players), left: "100%" }}
                >
                  {i18n._({ ...NEW_COUNT, values: { count: lastNew } })}
                </span>
              </>
            )}
            {hovered !== undefined && hover !== null && (
              <>
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute top-0 bottom-0 w-px bg-border-strong"
                  style={{ left: `${String((x(hover) / WIDTH) * 100)}%` }}
                />
                <span
                  aria-hidden="true"
                  className={cn(DOT, "bg-chart-blue")}
                  style={at(hover, hovered.active)}
                />
                <span
                  aria-hidden="true"
                  className={cn(DOT, "bg-chart-amber")}
                  style={at(hover, hovered.new_players)}
                />
                <div
                  role="status"
                  className={cn(
                    "pointer-events-none absolute top-2 z-10 flex flex-col gap-0.5 rounded-lg border border-border bg-surface-raised px-3 py-2 whitespace-nowrap shadow-[0_10px_28px_rgba(0,0,0,0.45)]",
                    hover > days.length / 2 ? "-translate-x-[calc(100%+14px)]" : "translate-x-3.5",
                  )}
                  style={{ left: `${String((x(hover) / WIDTH) * 100)}%` }}
                >
                  <span className="text-xs text-subtle">{long.format(dayDate(hovered.day))}</span>
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true" className="h-0.5 w-3 bg-chart-blue" />
                    <strong className="font-mono text-sm">{hovered.active}</strong>
                    <span className="text-xs text-muted">
                      {i18n._({ ...ACTIVE_WORD, values: { count: hovered.active } })}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true" className="h-0.5 w-3 bg-chart-amber" />
                    <strong className="font-mono text-sm">{hovered.new_players}</strong>
                    <span className="text-xs text-muted">
                      {i18n._({ ...NEW_WORD, values: { count: hovered.new_players } })}
                    </span>
                  </span>
                </div>
              </>
            )}
            <div className="absolute inset-0 flex">
              {days.map((day, index) => (
                <span
                  key={day.day}
                  className="h-full flex-1"
                  onMouseEnter={() => {
                    setHover(index);
                  }}
                />
              ))}
            </div>
          </div>
          <div aria-hidden="true" className="absolute right-[104px] bottom-0 left-[44px] h-[22px]">
            {days.map((day, index) =>
              (days.length - 1 - index) % xEvery === 0 ? (
                <span
                  key={day.day}
                  className="absolute top-1 -translate-x-1/2 text-[11px] whitespace-nowrap text-subtle"
                  style={{ left: `${String((x(index) / WIDTH) * 100)}%` }}
                >
                  {short.format(dayDate(day.day))}
                </span>
              ) : null,
            )}
          </div>
        </div>
      )}
    </DashboardCard>
  );
};
